import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HdIcon from "@mui/icons-material/Hd";
import FourKIcon from "@mui/icons-material/FourK";
import { FapHouseContext } from "../contexts/FapHouseContext";

// ─── Animations ────────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

// ─── Styled ────────────────────────────────────────────────────────────────────
const VideoCard = styled(Box)(({ theme }) => ({
  position: "relative",
  borderRadius: 10,
  overflow: "hidden",
  cursor: "pointer",
  flexShrink: 0,
  width: 200,
  [theme.breakpoints.down("sm")]: { width: 160 },
  "&:hover .fh-overlay": { opacity: 1 },
  "&:hover img": { transform: "scale(1.05)" },
}));

const Thumb = styled("img")({
  width: "100%",
  height: 120,
  objectFit: "cover",
  display: "block",
  transition: "transform 0.3s ease",
});

const Overlay = styled(Box)({
  className: "fh-overlay",
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: 0,
  transition: "opacity 0.2s ease",
});

const QualityBadge = styled(Box)(({ quality }) => ({
  position: "absolute",
  top: 6,
  right: 6,
  background: quality === "4k" ? "#9C27B0" : "#1565C0",
  color: "#fff",
  fontSize: "0.6rem",
  fontWeight: 800,
  padding: "1px 5px",
  borderRadius: 4,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
}));

const Duration = styled(Box)({
  position: "absolute",
  bottom: 36,
  right: 6,
  background: "rgba(0,0,0,0.75)",
  color: "#fff",
  fontSize: "0.65rem",
  padding: "1px 5px",
  borderRadius: 4,
});

const fmtDuration = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// ─── Main Component ────────────────────────────────────────────────────────────
const FapHouseSection = () => {
  const { videos, loading } = useContext(FapHouseContext);
  const [hovered, setHovered] = useState(null);
  const navigate = useNavigate();

  if (!loading && videos.length === 0) return null;

  const handleClick = (video) => {
    // Navigate to SEO detail page — Google can crawl these
    navigate(`/vod/${video.slug || video.id}`);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 800, mb: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
          🎬 Hot Videos
        </Typography>
        <Chip
          label="FapHouse"
          size="small"
          sx={{
            bgcolor: "#FF1744",
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.65rem",
            height: 20,
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            bgcolor: "#fce4ec",
            border: "1px solid #FF1744",
            borderRadius: "12px",
            px: 1,
            py: 0.3,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "#FF1744",
              animation: `${pulse} 1.2s ease-in-out infinite`,
            }}
          />
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#b71c1c" }}>
            HD &amp; 4K
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: "#aaa" }}>
          {loading ? "loading…" : `${videos.length} videos`}
        </Typography>
      </Box>

      {/* Horizontal scroll */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          overflowX: "auto",
          pb: 1,
          "&::-webkit-scrollbar": { height: 4 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#FF1744", borderRadius: 2 },
        }}
      >
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width={200}
                height={120}
                sx={{ borderRadius: 2, flexShrink: 0 }}
              />
            ))
          : videos.map((v) => (
              <VideoCard
                key={v.id}
                onClick={() => handleClick(v)}
                onMouseEnter={() => setHovered(v.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <Thumb
                  src={
                    hovered === v.id && v.previewUrl
                      ? undefined
                      : v.thumbnailUrl
                  }
                  alt={v.title}
                  onError={(e) => {
                    e.target.src = v.thumbnailUrl;
                  }}
                />
                {/* Hover: mp4 preview */}
                {hovered === v.id && v.previewUrl && (
                  <Box
                    component="video"
                    src={v.previewUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}

                <Overlay className="fh-overlay">
                  <PlayArrowIcon sx={{ color: "#fff", fontSize: 40 }} />
                </Overlay>

                <QualityBadge quality={v.quality}>
                  {v.quality === "4k" ? "4K" : "HD"}
                </QualityBadge>

                <Duration>{fmtDuration(v.duration)}</Duration>

                {/* Title */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: "linear-gradient(transparent, rgba(0,0,0,0.9))",
                    p: "4px 6px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {v.title}
                  </Typography>
                </Box>
              </VideoCard>
            ))}
      </Box>
    </Box>
  );
};

export default FapHouseSection;
